import React, { useState, useEffect, useCallback } from 'react';
import WrapperComponent from '../commonComponents/WrapperComponent';
import PaginationComponent from '../commonComponents/PaginationComponent';
import { Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import { url } from '../../util/config';

const CreditHistoryComponent = () => {
    const [currentBalance, setCurrentBalance] = useState(0);
    const [totalCredit, setTotalCredit] = useState(0);
    const [totalDebit, setTotalDebit] = useState(0);
    const [chartData, setChartData] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const itemsPerPage = 10;

    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
    const currentDate = today.toISOString().split('T')[0];

    const [fromDate, setFromDate] = useState(firstDayOfMonth);
    const [toDate, setToDate] = useState(currentDate);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${url}/balancehistory?from=${fromDate}&to=${toDate}`, {
                method: 'GET',
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!response.ok) throw new Error('Failed to fetch credit history data');
            const data = await response.json();
            setCurrentBalance(data.currentBalance);
            setTotalCredit(data.totalCredit);
            setTotalDebit(data.totalDebit);
            setChartData(data.chartData);
            setTableData(data.tableData);
            setCurrentPage(1);
        } catch (error) {
            console.error('Error fetching credit history data:', error);
            setError('Failed to load credit history. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, [fromDate, toDate]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const barChartData = {
        labels: chartData.map((item) => item.createdAt),
        datasets: [
            {
                label: 'Credit',
                data: chartData.map((item) => item.credit),
                backgroundColor: 'rgba(21, 101, 192, 0.75)',
                borderColor: '#1565c0',
                borderWidth: 1,
                borderRadius: 4,
            },
            {
                label: 'Debit',
                data: chartData.map((item) => item.debit),
                backgroundColor: 'rgba(220, 38, 38, 0.65)',
                borderColor: '#dc2626',
                borderWidth: 1,
                borderRadius: 4,
            },
        ],
    };

    const barChartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
                labels: { font: { size: 12, family: "'Segoe UI', system-ui" }, usePointStyle: true, pointStyleWidth: 8 }
            },
            tooltip: {
                callbacks: {
                    label: (ctx) => ` ₹${ctx.parsed.y.toLocaleString()}`
                }
            }
        },
        scales: {
            x: { grid: { display: false }, ticks: { font: { size: 11 } } },
            y: {
                grid: { color: '#f3f4f6' },
                ticks: {
                    font: { size: 11 },
                    callback: (val) => `₹${val.toLocaleString()}`
                }
            }
        }
    };

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentTableData = tableData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(tableData.length / itemsPerPage);

    const statCards = [
        {
            label: 'Current Balance',
            value: currentBalance,
            icon: 'bi-wallet2',
            color: '#1565c0',
            bg: '#eff6ff',
            border: '#bfdbfe',
        },
        {
            label: 'Total Credit',
            value: totalCredit,
            icon: 'bi-arrow-down-circle',
            color: '#16a34a',
            bg: '#f0fdf4',
            border: '#bbf7d0',
        },
        {
            label: 'Total Debit',
            value: totalDebit,
            icon: 'bi-arrow-up-circle',
            color: '#dc2626',
            bg: '#fef2f2',
            border: '#fecaca',
        },
    ];

    return (
        <WrapperComponent>
            <div style={styles.page}>
                {/* Page header */}
                <div style={styles.pageHeader}>
                    <div>
                        <h5 style={styles.pageTitle}>Credit History</h5>
                        <p style={styles.pageSubtitle}>Track your balance, credits and debits</p>
                    </div>
                </div>

                {/* Date filters */}
                <div style={styles.filterCard}>
                    <div style={styles.filterRow}>
                        <i className="bi bi-calendar3" style={{ color: '#6b7280', fontSize: 15 }}></i>
                        <div style={styles.filterGroup}>
                            <label style={styles.filterLabel}>From Date</label>
                            <input
                                type="date"
                                value={fromDate}
                                max={toDate}
                                style={styles.dateInput}
                                onChange={(e) => setFromDate(e.target.value)}
                            />
                        </div>
                        <span style={styles.filterSep}>→</span>
                        <div style={styles.filterGroup}>
                            <label style={styles.filterLabel}>To Date</label>
                            <input
                                type="date"
                                value={toDate}
                                min={fromDate}
                                max={currentDate}
                                style={styles.dateInput}
                                onChange={(e) => setToDate(e.target.value)}
                            />
                        </div>
                        <button style={styles.btnPrimary} onClick={fetchData} disabled={isLoading}>
                            {isLoading
                                ? <><span style={styles.spinner}></span> Loading…</>
                                : <><i className="bi bi-search" style={{ marginRight: 6 }}></i>Apply</>}
                        </button>
                    </div>
                </div>

                {/* Stat Cards */}
                <div style={styles.statRow}>
                    {statCards.map((card, i) => (
                        <div key={i} style={{ ...styles.statCard, border: `1px solid ${card.border}` }}>
                            <div style={{ ...styles.statIcon, background: card.bg, color: card.color }}>
                                <i className={`bi ${card.icon}`}></i>
                            </div>
                            <div style={styles.statInfo}>
                                <p style={styles.statLabel}>{card.label}</p>
                                <p style={{ ...styles.statValue, color: card.color }}>
                                    ₹{Number(card.value).toLocaleString()}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>

                {isLoading ? (
                    <div style={styles.centerState}>
                        <div className="spinner-border text-primary" style={{ width: '2rem', height: '2rem' }}></div>
                        <p style={styles.stateText}>Loading credit history…</p>
                    </div>
                ) : error ? (
                    <div style={styles.centerState}>
                        <i className="bi bi-exclamation-circle" style={{ fontSize: 32, color: '#ef4444' }}></i>
                        <p style={{ ...styles.stateText, color: '#ef4444' }}>{error}</p>
                        <button style={styles.btnPrimary} onClick={fetchData}>Retry</button>
                    </div>
                ) : (
                    <>
                        {/* Bar Chart */}
                        {chartData.length > 0 && (
                            <div style={styles.chartCard}>
                                <h6 style={styles.sectionTitle}>
                                    <i className="bi bi-bar-chart" style={{ marginRight: 8, color: '#1565c0' }}></i>
                                    Date-wise Credit & Debit
                                </h6>
                                <div style={{ padding: '0 8px 8px' }}>
                                    <Bar data={barChartData} options={barChartOptions} />
                                </div>
                            </div>
                        )}

                        {/* Transaction Table */}
                        <div style={styles.tableCard}>
                            <div style={styles.tableHeader}>
                                <h6 style={styles.sectionTitle}>
                                    <i className="bi bi-list-ul" style={{ marginRight: 8, color: '#1565c0' }}></i>
                                    Transaction History
                                </h6>
                                <span style={styles.resultCount}>
                                    {tableData.length} transaction{tableData.length !== 1 ? 's' : ''}
                                </span>
                            </div>

                            {tableData.length === 0 ? (
                                <div style={styles.centerState}>
                                    <i className="bi bi-receipt" style={{ fontSize: 36, color: '#d1d5db' }}></i>
                                    <p style={styles.stateText}>No transactions found for the selected date range.</p>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table style={styles.table}>
                                        <thead>
                                            <tr style={styles.tableHead}>
                                                <th style={styles.th}>Type</th>
                                                <th style={styles.th}>Mode</th>
                                                <th style={styles.th}>Amount</th>
                                                <th style={styles.th}>By</th>
                                                <th style={styles.th}>Date</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentTableData.map((item, index) => (
                                                <tr key={index} style={styles.tr}>
                                                    <td style={styles.td}>
                                                        <span style={{
                                                            ...styles.typeBadge,
                                                            ...(item.action?.toLowerCase() === 'credit'
                                                                ? styles.creditBadge
                                                                : styles.debitBadge)
                                                        }}>
                                                            <i className={`bi ${item.action?.toLowerCase() === 'credit' ? 'bi-arrow-down-circle' : 'bi-arrow-up-circle'}`}
                                                                style={{ marginRight: 5 }}></i>
                                                            {item.action}
                                                        </span>
                                                    </td>
                                                    <td style={styles.td}>{item.mode || '—'}</td>
                                                    <td style={{ ...styles.td, fontWeight: '600', color: item.action?.toLowerCase() === 'credit' ? '#16a34a' : '#dc2626' }}>
                                                        ₹{Number(item.balance).toLocaleString()}
                                                    </td>
                                                    <td style={styles.td}>{item.added_by}</td>
                                                    <td style={styles.td}>{item.createdAt}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        {tableData.length > 0 && (
                            <PaginationComponent
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={(page) => setCurrentPage(page)}
                            />
                        )}
                    </>
                )}
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </WrapperComponent>
    );
};

const styles = {
    page: { display: 'flex', flexDirection: 'column', gap: '16px', fontFamily: "'Segoe UI', system-ui, sans-serif" },
    pageHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' },
    pageTitle: { margin: 0, fontSize: '20px', fontWeight: '700', color: '#111827' },
    pageSubtitle: { margin: '2px 0 0', fontSize: '13px', color: '#6b7280' },
    filterCard: { background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '16px 20px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' },
    filterRow: { display: 'flex', alignItems: 'flex-end', gap: '16px', flexWrap: 'wrap' },
    filterGroup: { display: 'flex', flexDirection: 'column', gap: 4 },
    filterLabel: { fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' },
    filterSep: { fontSize: '16px', color: '#9ca3af', paddingBottom: '6px' },
    dateInput: { padding: '8px 12px', fontSize: '13px', border: '1.5px solid #e5e7eb', borderRadius: '8px', outline: 'none', color: '#374151' },
    statRow: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' },
    statCard: { background: 'white', borderRadius: '12px', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '16px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' },
    statIcon: { width: '48px', height: '48px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', flexShrink: 0 },
    statInfo: { display: 'flex', flexDirection: 'column', gap: 2 },
    statLabel: { margin: 0, fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em' },
    statValue: { margin: 0, fontSize: '22px', fontWeight: '700' },
    chartCard: { background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '20px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' },
    tableCard: { background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' },
    tableHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #f3f4f6' },
    sectionTitle: { margin: 0, fontSize: '14px', fontWeight: '700', color: '#111827', display: 'flex', alignItems: 'center' },
    resultCount: { fontSize: '12px', color: '#6b7280', background: '#f3f4f6', padding: '3px 10px', borderRadius: '20px' },
    centerState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '60px 20px' },
    stateText: { fontSize: '14px', color: '#6b7280', margin: 0 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
    tableHead: { background: '#eff6ff' },
    th: { padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#1565c0', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '2px solid #bfdbfe', whiteSpace: 'nowrap' },
    tr: { borderBottom: '1px solid #f3f4f6' },
    td: { padding: '12px 16px', color: '#374151', verticalAlign: 'middle' },
    typeBadge: { display: 'inline-flex', alignItems: 'center', padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' },
    creditBadge: { background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' },
    debitBadge: { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' },
    btnPrimary: { display: 'inline-flex', alignItems: 'center', padding: '8px 16px', background: 'linear-gradient(to right, #2388e0, #1565c0)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
    spinner: { width: '14px', height: '14px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite', marginRight: 6 },
};

export default CreditHistoryComponent;
